/**
 * Excel functions the formula engine (@fortune-sheet/formula-parser 0.2.13) lacks. The core
 * patch `formula-functions` hands every parser it creates to the installer registered at the
 * bottom of this module, so each workbook's parser has them before it evaluates anything.
 *
 * - Modern lookups and conditionals: XLOOKUP, XMATCH, MAXIFS, MINIFS, TEXTJOIN.
 * - Excel 2010+ dotted names, mapped onto the engine's own implementation where one exists
 *   (RANK.EQ → RANK, STDEV.S → STDEV, …) and computed here where Excel's rule differs.
 *
 * Arguments arrive as the engine evaluates them: a range as a 2-D array of cell values, a
 * single cell or literal as a scalar, an empty cell as null/undefined. An error is reported by
 * throwing the engine's error code ('N/A', 'VALUE', …), which it renders as #N/A, #VALUE!, ….
 */

export type FormulaFunction = (params: unknown[]) => unknown;

/** The slice of the engine's Parser this module uses. */
export interface FormulaParser {
    setFunction(name: string, fn: FormulaFunction): unknown;
    getFunction(name: string): FormulaFunction | undefined;
    functions?: Record<string, FormulaFunction>;
    /** Calls a function by name, falling back to the engine's built-ins. */
    _callFunction?(name: string, params: unknown[]): unknown;
    /** Resolves a cell label (`A1`, `TRUE`, …) while parsing. */
    _callCellValue?(label: string): unknown;
}

const ERR = { NA: 'N/A', VALUE: 'VALUE', NUM: 'NUM', DIV0: 'DIV/0' } as const;
const fail = (code: (typeof ERR)[keyof typeof ERR]): never => {
    throw new Error(code);
};

const flatten = (value: unknown): unknown[] => (Array.isArray(value) ? value.flatMap((item) => flatten(item)) : [value]);
const isBlank = (value: unknown): boolean => value === null || value === undefined || value === '';
const isOmitted = (value: unknown): boolean => value === undefined;

const toNumber = (value: unknown): number => {
    if (typeof value === 'number') return value;
    if (typeof value === 'boolean') return value ? 1 : 0;
    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) return Number(value);
    return NaN;
};

const toText = (value: unknown): string => {
    if (isBlank(value)) return '';
    if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
    return String(value);
};

const toBool = (value: unknown): boolean =>
    typeof value === 'string' ? value.toUpperCase() === 'TRUE' : Boolean(toNumber(value) || value === true);

/** Excel wildcards: * any run, ? one character, ~ escapes the next character. */
const wildcardRegExp = (pattern: string): RegExp => {
    let out = '';
    for (let i = 0; i < pattern.length; i += 1) {
        const ch = pattern[i];
        if (ch === '~' && i + 1 < pattern.length) {
            i += 1;
            out += pattern[i].replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        } else if (ch === '*') out += '[\\s\\S]*';
        else if (ch === '?') out += '[\\s\\S]';
        else out += ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
    return new RegExp(`^${out}$`, 'i');
};

/** Equal as Excel's lookups compare: a number only to a number (5 never matches "5"), text case-insensitively. */
const sameValue = (a: unknown, b: unknown): boolean => {
    if (typeof a === 'number' || typeof b === 'number') return typeof a === 'number' && typeof b === 'number' && a === b;
    if (typeof a === 'boolean' || typeof b === 'boolean') return a === b;
    return toText(a).toLowerCase() === toText(b).toLowerCase();
};

/** Excel's criteria for *IFS functions: "", "=x", "<>x", ">5", "<=3", wildcards, a number. */
export const matchesCriterion = (value: unknown, criterion: unknown): boolean => {
    if (typeof criterion === 'number') return toNumber(value) === criterion && typeof value !== 'boolean';
    if (typeof criterion === 'boolean') return value === criterion;
    const text = toText(criterion);
    const op = /^(<=|>=|<>|=|<|>)/.exec(text)?.[1] ?? '';
    const operand = text.slice(op.length);
    if (op === '' && operand === '') return isBlank(value);
    const target = toNumber(operand);
    const numeric = operand.trim() !== '' && !Number.isNaN(target);
    if (op === '<' || op === '>' || op === '<=' || op === '>=') {
        if (numeric) {
            const n = typeof value === 'number' ? value : NaN;
            if (Number.isNaN(n)) return false;
            return op === '<' ? n < target : op === '>' ? n > target : op === '<=' ? n <= target : n >= target;
        }
        if (typeof value !== 'string') return false;
        const cmp = value.toLowerCase().localeCompare(operand.toLowerCase());
        return op === '<' ? cmp < 0 : op === '>' ? cmp > 0 : op === '<=' ? cmp <= 0 : cmp >= 0;
    }
    let equal: boolean;
    if (operand === '') equal = isBlank(value);
    else if (numeric && typeof value === 'number') equal = value === target;
    else if (numeric && typeof value !== 'string') equal = false;
    else equal = wildcardRegExp(operand).test(toText(value));
    return op === '<>' ? !equal : equal;
};

/** Values of max/min_range whose criteria all hold (MAXIFS/MINIFS). */
const conditionalNumbers = (params: unknown[]): number[] => {
    const [range, ...pairs] = params;
    if (pairs.length === 0 || pairs.length % 2 !== 0) fail(ERR.VALUE);
    const values = flatten(range);
    const tests: { cells: unknown[]; criterion: unknown }[] = [];
    for (let i = 0; i < pairs.length; i += 2) {
        const cells = flatten(pairs[i]);
        if (cells.length !== values.length) fail(ERR.VALUE);
        tests.push({ cells, criterion: pairs[i + 1] });
    }
    return values.filter(
        (value, i) => typeof value === 'number' && tests.every(({ cells, criterion }) => matchesCriterion(cells[i], criterion)),
    ) as number[];
};

/** A 1-D lookup vector from a single row or column range. */
const vector = (value: unknown): unknown[] => {
    if (!Array.isArray(value)) return [value];
    const rows = value as unknown[][];
    if (rows.length === 1 || rows.every((row) => !Array.isArray(row) || row.length === 1)) return flatten(rows);
    return fail(ERR.VALUE);
};

/**
 * Index of `needle` in `haystack` per XLOOKUP/XMATCH's match_mode (0 exact, -1 exact or next
 * smaller, 1 exact or next larger, 2 wildcard) and search_mode (1 first to last, -1 last to
 * first; the binary modes 2/-2 assume sorted data and give the same answer searched linearly).
 */
const lookupIndex = (needle: unknown, haystack: unknown[], matchMode: number, searchMode: number): number => {
    const order = searchMode < 0 ? haystack.map((_, i) => haystack.length - 1 - i) : haystack.map((_, i) => i);
    if (matchMode === 2 && typeof needle === 'string') {
        const re = wildcardRegExp(needle);
        return order.find((i) => re.test(toText(haystack[i]))) ?? -1;
    }
    const exact = order.find((i) => sameValue(haystack[i], needle));
    if (exact !== undefined || matchMode === 0 || matchMode === 2) return exact ?? -1;
    const numericNeedle = toNumber(needle);
    const byNumber = typeof needle === 'number' || !Number.isNaN(numericNeedle);
    let best = -1;
    order.forEach((i) => {
        const v = haystack[i];
        if (byNumber ? typeof v !== 'number' : typeof v !== 'string') return;
        const smaller = byNumber ? (v as number) < numericNeedle : toText(v).toLowerCase() < toText(needle).toLowerCase();
        if (matchMode === -1 ? !smaller : smaller) return;
        if (best === -1) {
            best = i;
            return;
        }
        const b = haystack[best];
        const closer = byNumber
            ? matchMode === -1
                ? (v as number) > (b as number)
                : (v as number) < (b as number)
            : matchMode === -1
            ? toText(v).toLowerCase() > toText(b).toLowerCase()
            : toText(v).toLowerCase() < toText(b).toLowerCase();
        if (closer) best = i;
    });
    return best;
};

const modeArg = (value: unknown, fallback: number): number => (isOmitted(value) || isBlank(value) ? fallback : toNumber(value));

/** XLOOKUP(lookup_value, lookup_array, return_array, [if_not_found], [match_mode], [search_mode]). */
const XLOOKUP: FormulaFunction = ([needle, lookupArray, returnArray, ifNotFound, matchMode, searchMode]) => {
    const haystack = vector(lookupArray);
    const index = lookupIndex(needle, haystack, modeArg(matchMode, 0), modeArg(searchMode, 1));
    if (index === -1) return isOmitted(ifNotFound) ? fail(ERR.NA) : ifNotFound;
    if (!Array.isArray(returnArray)) return returnArray;
    const rows = (returnArray as unknown[]).map((row) => (Array.isArray(row) ? row : [row]));
    const lookupIsRow = Array.isArray(lookupArray) && (lookupArray as unknown[]).length === 1 && haystack.length > 1;
    // Column `index` of a row lookup, row `index` of a column lookup. A return array several
    // columns (rows) wide spills in Excel; without spilling, its first value is shown.
    const row = lookupIsRow ? rows[0] : rows[index];
    const picked = lookupIsRow ? row?.[index] : row?.[0];
    return picked === undefined ? fail(ERR.VALUE) : picked;
};

/** XMATCH(lookup_value, lookup_array, [match_mode], [search_mode]): 1-based position. */
const XMATCH: FormulaFunction = ([needle, lookupArray, matchMode, searchMode]) => {
    const index = lookupIndex(needle, vector(lookupArray), modeArg(matchMode, 0), modeArg(searchMode, 1));
    return index === -1 ? fail(ERR.NA) : index + 1;
};

/** TEXTJOIN(delimiter, ignore_empty, text1, [text2], …). */
const TEXTJOIN: FormulaFunction = ([delimiter, ignoreEmpty, ...texts]) => {
    const delimiters = flatten(delimiter).map(toText);
    const parts = texts.flatMap((text) => flatten(text)).map(toText);
    const kept = toBool(ignoreEmpty) ? parts.filter((part) => part !== '') : parts;
    return kept.reduce((out, part, i) => (i === 0 ? part : out + delimiters[(i - 1) % delimiters.length] + part), '');
};

// Not `Math.max(...numbers)`: spreading a whole-column match (100k+ values) overflows the call stack.
const MAXIFS: FormulaFunction = (params) => {
    const numbers = conditionalNumbers(params);
    return numbers.length ? numbers.reduce((max, n) => (n > max ? n : max), -Infinity) : 0;
};

const MINIFS: FormulaFunction = (params) => {
    const numbers = conditionalNumbers(params);
    return numbers.length ? numbers.reduce((min, n) => (n < min ? n : min), Infinity) : 0;
};

const numbersOf = (values: unknown): number[] => flatten(values).filter((value): value is number => typeof value === 'number');

/** Excel's rounding to a multiple: CEILING.MATH / FLOOR.MATH / *.PRECISE. */
const roundToMultiple = (number: number, significance: number, up: boolean): number => {
    if (significance === 0) return 0;
    const step = Math.abs(significance);
    const q = number / step;
    const rounded = (up ? Math.ceil(q - 1e-12) : Math.floor(q + 1e-12)) * step;
    return Number(rounded.toPrecision(15));
};

const CEILING_MATH: FormulaFunction = ([number, significance, mode]) => {
    const n = toNumber(number);
    if (Number.isNaN(n)) fail(ERR.VALUE);
    const s = isOmitted(significance) ? 1 : toNumber(significance);
    // A negative number rounds toward zero, or away from it when mode is non-zero.
    const awayFromZero = n < 0 && !isOmitted(mode) && toNumber(mode) !== 0;
    return roundToMultiple(n, s, !awayFromZero);
};

const FLOOR_MATH: FormulaFunction = ([number, significance, mode]) => {
    const n = toNumber(number);
    if (Number.isNaN(n)) fail(ERR.VALUE);
    const s = isOmitted(significance) ? 1 : toNumber(significance);
    const towardZero = n < 0 && !isOmitted(mode) && toNumber(mode) !== 0;
    return roundToMultiple(n, s, towardZero);
};

const precise =
    (up: boolean): FormulaFunction =>
    ([number, significance]) => {
        const n = toNumber(number);
        if (Number.isNaN(n)) fail(ERR.VALUE);
        return roundToMultiple(n, isOmitted(significance) ? 1 : toNumber(significance), up);
    };

/** NORM.S.DIST(z, cumulative). */
const NORM_S_DIST =
    (callBuiltin: (name: string, params: unknown[]) => unknown): FormulaFunction =>
    ([z, cumulative]) => {
        const x = toNumber(z);
        if (Number.isNaN(x)) fail(ERR.VALUE);
        // The engine's NORMSDIST takes Excel 2010's cumulative flag; without it, it gives the density.
        return toBool(cumulative) ? callBuiltin('NORMSDIST', [x, true]) : Math.exp((-x * x) / 2) / Math.sqrt(2 * Math.PI);
    };

/** RANK.AVG(number, ref, [order]): ties share the average of their ranks. */
const RANK_AVG: FormulaFunction = ([number, ref, order]) => {
    const n = toNumber(number);
    const values = numbersOf(ref);
    if (Number.isNaN(n) || !values.includes(n)) fail(ERR.NA);
    const ascending = !isOmitted(order) && toNumber(order) !== 0;
    const better = values.filter((v) => (ascending ? v < n : v > n)).length;
    const ties = values.filter((v) => v === n).length;
    return better + (ties + 1) / 2;
};

/** PERCENTILE.EXC(array, k): k strictly between 1/(n+1) and n/(n+1). */
const PERCENTILE_EXC: FormulaFunction = ([array, k]) => {
    const values = numbersOf(array).sort((a, b) => a - b);
    const p = toNumber(k);
    const n = values.length;
    if (!n || Number.isNaN(p) || p <= 0 || p >= 1) return fail(ERR.NUM);
    const rank = p * (n + 1);
    if (rank < 1 || rank > n) return fail(ERR.NUM);
    const lower = Math.floor(rank);
    const frac = rank - lower;
    return lower >= n ? values[n - 1] : values[lower - 1] + frac * (values[lower] - values[lower - 1]);
};

/** COVARIANCE.S(array1, array2): sample covariance. */
const COVARIANCE_S: FormulaFunction = ([a, b]) => {
    const xs = flatten(a);
    const ys = flatten(b);
    if (xs.length !== ys.length) return fail(ERR.NA);
    const pairs = xs.map((x, i) => [x, ys[i]]).filter(([x, y]) => typeof x === 'number' && typeof y === 'number') as [number, number][];
    if (pairs.length < 2) return fail(ERR.DIV0);
    const mx = pairs.reduce((s, [x]) => s + x, 0) / pairs.length;
    const my = pairs.reduce((s, [, y]) => s + y, 0) / pairs.length;
    return pairs.reduce((s, [x, y]) => s + (x - mx) * (y - my), 0) / (pairs.length - 1);
};

/** Dotted Excel names whose rule is the engine's own older function. */
const ALIASES: Record<string, string> = {
    'RANK.EQ': 'RANK',
    'STDEV.S': 'STDEV',
    'STDEV.P': 'STDEVP',
    'VAR.S': 'VAR',
    'VAR.P': 'VARP',
    'MODE.SNGL': 'MODE',
    'PERCENTILE.INC': 'PERCENTILE',
    'QUARTILE.INC': 'QUARTILE',
    'NORM.DIST': 'NORMDIST',
    'NORM.INV': 'NORMINV',
    'NORM.S.INV': 'NORMSINV',
    'COVARIANCE.P': 'COVAR',
    'FORECAST.LINEAR': 'FORECAST',
};

/** The functions this module adds, with built-in calls resolved through `parser`. */
export const extraFormulaFunctions = (parser: FormulaParser): Record<string, FormulaFunction> => {
    const callBuiltin = (name: string, params: unknown[]): unknown =>
        parser._callFunction ? parser._callFunction(name, params) : fail(ERR.VALUE);
    const aliases = Object.fromEntries(
        Object.entries(ALIASES).map(([name, target]) => [name, (params: unknown[]) => callBuiltin(target, params)]),
    );
    return {
        ...aliases,
        XLOOKUP,
        XMATCH,
        TEXTJOIN,
        MAXIFS,
        MINIFS,
        'CEILING.MATH': CEILING_MATH,
        'FLOOR.MATH': FLOOR_MATH,
        'CEILING.PRECISE': precise(true),
        'FLOOR.PRECISE': precise(false),
        'NORM.S.DIST': NORM_S_DIST(callBuiltin),
        'RANK.AVG': RANK_AVG,
        'PERCENTILE.EXC': PERCENTILE_EXC,
        'COVARIANCE.S': COVARIANCE_S,
    };
};

/** The names (upper case) this module adds — for tests and the function list. */
export const EXTRA_FORMULA_FUNCTION_NAMES = Object.keys(extraFormulaFunctions({} as FormulaParser));

/**
 * Registers the functions on `parser`, makes its lookups case-insensitive (=xlookup(…)), and reads
 * a bare TRUE / FALSE as the boolean: the engine lexes them as a column-only cell label and
 * answered #NAME?, so =VLOOKUP(x, range, 2, FALSE) or =IF(a, TRUE, FALSE) failed once recalculated.
 * No column is called TRUE or FALSE (the last one is XFD).
 */
export const installFormulaFunctions = (parser: FormulaParser): void => {
    Object.entries(extraFormulaFunctions(parser)).forEach(([name, fn]) => parser.setFunction(name, fn));
    const getOwn = parser.getFunction.bind(parser);
    // eslint-disable-next-line no-param-reassign
    parser.getFunction = (name: string) => getOwn(name) ?? getOwn(String(name).toUpperCase());
    const cellValue = parser._callCellValue?.bind(parser);
    if (!cellValue) return;
    // eslint-disable-next-line no-param-reassign
    parser._callCellValue = (label: string) => {
        const upper = String(label).toUpperCase();
        if (upper === 'TRUE' || upper === 'FALSE') return upper === 'TRUE';
        return cellValue(label);
    };
};

// The core patch `formula-functions` calls this for every parser it creates.
(globalThis as unknown as { __aicaInstallFormulaFunctions?: (parser: FormulaParser) => void }).__aicaInstallFormulaFunctions =
    installFormulaFunctions;
